import { i18n } from '@konova/i18n';
import { Children, cloneElement, FC, ReactElement, ReactNode } from 'react';
import { useConfirmation } from '../../hooks/use-confirmation';
import { CheckboxProps } from '../checkbox';
import { ConfirmationModalActions } from '../confirmation-modal';
import { ModalProps } from '../modal';

export type RequireConfirmationChildProps = { onClick?: (e: unknown) => void };

export type RequireConfirmationProps = {
  title?: string;
  description?: ReactNode;
  children: ReactElement<RequireConfirmationChildProps>;

  /**
   * Represents the state or properties of a checkbox input.
   * This variable can be either a boolean or an object containing specific checkbox properties.
   *
   * - If set to a boolean, it determines the checked state of the checkbox:
   *   - `true`: The checkbox is checked.
   *   - `false`: The checkbox is unchecked.
   * - If set to an object of type `CheckboxProps`, it allows customization of the checkbox by specifying additional properties.
   *
   * This is commonly used for conditional rendering or when more dynamic customization is needed for checkbox components.
   */
  checkbox?: boolean | CheckboxProps;

  /**
   * Actions available for the confirmation modal.
   *
   * This variable represents a set of actions or operations that can be performed
   * within a confirmation modal component. It can include callbacks or handlers
   * for actions like confirming, canceling, or other custom operations provided
   * by the user of the modal.
   */
  actions?: ConfirmationModalActions;

  confirmButton?: ReactElement;

  /**
   * Specifies the size of the modal. This property is optional and determines
   * the dimensions or styling of the modal based on predefined sizes.
   * It inherits the 'size' type from `ModalProps`.
   */
  size?: ModalProps['size'];
};

/**
 * RequireConfirmation is a functional component that wraps its child component
 * and provides a confirmation mechanism before triggering the child component's onClick event.
 *
 * This component is designed to present a confirmation dialog with a title and description
 * when the wrapped child component is interacted with. If the user confirms the action,
 * the original onClick function of the child component is executed.
 * The behavior ensures any critical or irreversible actions are verified by user input.
 *
 * Key functionality:
 * - Uses `useConfirmation` to manage the state and behavior of the confirmation dialog.
 * - Clones the child component and overrides its onClick event to show the confirmation dialog.
 */
export const RequireConfirmation: FC<RequireConfirmationProps> = ({
  title = i18n.t<string>('common.general.delete-confirm.title'),
  description = i18n.t<string>('common.general.delete-confirm.description'),
  children,
  checkbox,
  actions,
  confirmButton,
  size
}) => {
  const item: ReactElement<RequireConfirmationChildProps> = Children.toArray(children)[0] as ReactElement<RequireConfirmationChildProps>;

  const confirmation = useConfirmation({
    title,
    description,
    checkbox,
    actions,
    confirmButton,
    size,
    onConfirm: e => item.props.onClick!(e)
  });

  return (
    <>
      {confirmation.component}
      {cloneElement(item, {
        ...item.props,
        onClick: e => confirmation.show(true, e)
      })}
    </>
  );
};